import {
  type EnvironmentId,
  type OrchestrationShellSnapshot,
  type ThreadId,
} from "@cmmd-forge/contracts";
import { scopeThreadRef } from "@cmmd-forge/client-runtime";
import { selectSidebarThreadSummaryByRef, selectThreadByRef, useStore } from "~/store";
import { useSavedEnvironmentRuntimeStore } from "./savedEnvironmentRuntimeStore";
import {
  threadDetailSubscriptions,
  type ThreadDetailSubscriptionEntry,
} from "./threadDetailSubscriptionState";
import {
  attachThreadDetailSubscription,
  clearThreadDetailSubscriptionEviction,
  disposeThreadDetailSubscriptionByKey,
  getThreadDetailSubscriptionKey,
  MAX_CACHED_THREAD_DETAIL_SUBSCRIPTIONS,
  NOOP,
  shouldEvictThreadDetailSubscription,
  startThreadDetailLivenessWatchdog,
  THREAD_DETAIL_SUBSCRIPTION_IDLE_EVICTION_MS,
  watchThreadDetailSubscriptionConnection,
} from "./threadDetailLivenessService";

/** Closes everything held for an id no connection will register under again. */
export function retireEnvironmentId(environmentId: EnvironmentId): void {
  disposeThreadDetailSubscriptionsForEnvironment(environmentId, true);
  useSavedEnvironmentRuntimeStore.getState().clear(environmentId);
}

/** `retire` closes retained entries too, not only the idle ones. */
export function disposeThreadDetailSubscriptionsForEnvironment(
  id: EnvironmentId,
  retire = false,
): void {
  for (const [key, entry] of threadDetailSubscriptions) {
    if (entry.environmentId !== id) continue;
    if (entry.refCount === 0 || retire) {
      disposeThreadDetailSubscriptionByKey(key);
      continue;
    }
    // A retained entry outlives its connection and rebinds to the next one.
    entry.unsubscribe();
    entry.unsubscribe = NOOP;
    watchThreadDetailSubscriptionConnection(entry);
  }
}

type RetainedThread = NonNullable<ReturnType<typeof selectThreadByRef>>;

function latestUserMessageAt(thread: RetainedThread): string | null {
  for (let index = thread.messages.length - 1; index >= 0; index -= 1) {
    const message = thread.messages[index];
    if (message?.role === "user") return message.createdAt;
  }
  return null;
}

function retainedShellSession(
  thread: RetainedThread,
): OrchestrationShellSnapshot["threads"][number]["session"] {
  if (!thread.session) return null;
  return {
    threadId: thread.id,
    updatedAt: thread.session.updatedAt,
    providerName: thread.session.provider,
    runtimeMode: thread.runtimeMode,
    activeTurnId: thread.session.activeTurnId ?? null,
    lastError: thread.session.lastError ?? null,
    status: thread.session.orchestrationStatus,
  };
}

function retainedShellOptionalFields(
  thread: RetainedThread,
  sidebarThread: ReturnType<typeof selectSidebarThreadSummaryByRef>,
) {
  return {
    updatedAt: thread.updatedAt ?? thread.createdAt,
    latestUserMessageAt: sidebarThread?.latestUserMessageAt ?? latestUserMessageAt(thread),
    titleRegeneration: thread.titleRegeneration ?? null,
  };
}

function retainedShellPendingFlags(
  sidebarThread: ReturnType<typeof selectSidebarThreadSummaryByRef>,
) {
  return {
    hasPendingApprovals: sidebarThread?.hasPendingApprovals ?? false,
    hasPendingUserInput: sidebarThread?.hasPendingUserInput ?? false,
    hasActionableProposedPlan: sidebarThread?.hasActionableProposedPlan ?? false,
  };
}

function buildRetainedShellThread(
  entry: ThreadDetailSubscriptionEntry,
): OrchestrationShellSnapshot["threads"][number] | null {
  const threadRef = scopeThreadRef(entry.environmentId, entry.threadId);
  const state = useStore.getState();
  const thread = selectThreadByRef(state, threadRef);
  if (!thread) {
    return null;
  }

  const sidebarThread = selectSidebarThreadSummaryByRef(state, threadRef);

  return {
    id: thread.id,
    projectId: thread.projectId,
    title: thread.title,
    modelSelection: thread.modelSelection,
    runtimeMode: thread.runtimeMode,
    interactionMode: thread.interactionMode,
    branch: thread.branch,
    worktreePath: thread.worktreePath,
    latestTurn: thread.latestTurn,
    createdAt: thread.createdAt,
    ...retainedShellOptionalFields(thread, sidebarThread),
    ...retainedShellPendingFlags(sidebarThread),
    archivedAt: thread.archivedAt,
    session: retainedShellSession(thread),
    anchorKind: thread.anchorKind,
    anchorCmmdId: thread.anchorCmmdId,
    kind: thread.kind,
    parentThreadId: thread.parentThreadId,
    status: thread.status,
    workerRole: thread.workerRole,
    goal: thread.goal,
  };
}

export function mergeShellSnapshotWithRetainedThreads(
  snapshot: OrchestrationShellSnapshot,
  environmentId: EnvironmentId,
): OrchestrationShellSnapshot {
  if (threadDetailSubscriptions.size === 0) {
    return snapshot;
  }

  const seenThreadIds = new Set(snapshot.threads.map((thread) => thread.id));
  const retainedThreads: Array<OrchestrationShellSnapshot["threads"][number]> = [];

  for (const entry of threadDetailSubscriptions.values()) {
    if (
      entry.environmentId !== environmentId ||
      entry.refCount <= 0 ||
      seenThreadIds.has(entry.threadId)
    ) {
      continue;
    }
    const retainedThread = buildRetainedShellThread(entry);
    if (!retainedThread) {
      continue;
    }
    seenThreadIds.add(retainedThread.id);
    retainedThreads.push(retainedThread);
  }

  if (retainedThreads.length === 0) {
    return snapshot;
  }

  return {
    ...snapshot,
    threads: [...snapshot.threads, ...retainedThreads],
  };
}

export function reconcileThreadDetailSubscriptionsForEnvironment(
  environmentId: EnvironmentId,
  threadIds: ReadonlyArray<ThreadId>,
): void {
  const activeThreadIds = new Set(threadIds);
  for (const [key, entry] of threadDetailSubscriptions) {
    if (
      entry.environmentId === environmentId &&
      !activeThreadIds.has(entry.threadId) &&
      shouldEvictThreadDetailSubscription(entry)
    ) {
      disposeThreadDetailSubscriptionByKey(key);
    }
  }
}

function scheduleThreadDetailSubscriptionEviction(entry: ThreadDetailSubscriptionEntry): void {
  clearThreadDetailSubscriptionEviction(entry);
  if (!shouldEvictThreadDetailSubscription(entry)) {
    return;
  }

  entry.evictionTimeoutId = setTimeout(() => {
    const currentEntry = threadDetailSubscriptions.get(
      getThreadDetailSubscriptionKey(entry.environmentId, entry.threadId),
    );
    if (!currentEntry) {
      return;
    }

    currentEntry.evictionTimeoutId = null;
    if (!shouldEvictThreadDetailSubscription(currentEntry)) {
      return;
    }
    disposeThreadDetailSubscriptionByKey(
      getThreadDetailSubscriptionKey(entry.environmentId, entry.threadId),
    );
  }, THREAD_DETAIL_SUBSCRIPTION_IDLE_EVICTION_MS);
}

export function evictIdleThreadDetailSubscriptionsToCapacity(): void {
  if (threadDetailSubscriptions.size <= MAX_CACHED_THREAD_DETAIL_SUBSCRIPTIONS) {
    return;
  }

  const idleEntries = [...threadDetailSubscriptions.entries()]
    .filter(([, entry]) => shouldEvictThreadDetailSubscription(entry))
    .toSorted(([, left], [, right]) => left.lastAccessedAt - right.lastAccessedAt);

  for (const [key] of idleEntries) {
    if (threadDetailSubscriptions.size <= MAX_CACHED_THREAD_DETAIL_SUBSCRIPTIONS) {
      return;
    }
    disposeThreadDetailSubscriptionByKey(key);
  }
}

function reconcileThreadDetailSubscriptionEvictionState(
  entry: ThreadDetailSubscriptionEntry,
): void {
  clearThreadDetailSubscriptionEviction(entry);
  if (!shouldEvictThreadDetailSubscription(entry)) {
    return;
  }

  scheduleThreadDetailSubscriptionEviction(entry);
}

export function reconcileThreadDetailSubscriptionEvictionForThread(
  environmentId: EnvironmentId,
  threadId: ThreadId,
): void {
  const entry = threadDetailSubscriptions.get(
    getThreadDetailSubscriptionKey(environmentId, threadId),
  );
  if (!entry) {
    return;
  }

  reconcileThreadDetailSubscriptionEvictionState(entry);
}

export function reconcileThreadDetailSubscriptionEvictionForEnvironment(
  environmentId: EnvironmentId,
): void {
  for (const entry of threadDetailSubscriptions.values()) {
    if (entry.environmentId === environmentId) {
      reconcileThreadDetailSubscriptionEvictionState(entry);
    }
  }
  evictIdleThreadDetailSubscriptionsToCapacity();
}

function createThreadDetailSubscriptionRelease(entry: ThreadDetailSubscriptionEntry): () => void {
  let released = false;
  return () => {
    if (released) {
      return;
    }
    released = true;
    entry.refCount = Math.max(0, entry.refCount - 1);
    entry.lastAccessedAt = Date.now();
    if (entry.refCount === 0) {
      reconcileThreadDetailSubscriptionEvictionState(entry);
      evictIdleThreadDetailSubscriptionsToCapacity();
    }
  };
}

export function retainThreadDetailSubscription(
  environmentId: EnvironmentId,
  threadId: ThreadId,
): () => void {
  const key = getThreadDetailSubscriptionKey(environmentId, threadId);
  const existing = threadDetailSubscriptions.get(key);
  if (existing) {
    clearThreadDetailSubscriptionEviction(existing);
    existing.refCount += 1;
    existing.lastAccessedAt = Date.now();
    if (!attachThreadDetailSubscription(existing)) {
      watchThreadDetailSubscriptionConnection(existing);
    }
    return createThreadDetailSubscriptionRelease(existing);
  }

  const entry: ThreadDetailSubscriptionEntry = {
    environmentId,
    threadId,
    unsubscribe: NOOP,
    unsubscribeConnectionListener: null,
    refCount: 1,
    lastAccessedAt: Date.now(),
    evictionTimeoutId: null,
    lastEventAt: Date.now(),
    recoveryAttempts: 0,
  };
  threadDetailSubscriptions.set(key, entry);
  if (!attachThreadDetailSubscription(entry)) {
    watchThreadDetailSubscriptionConnection(entry);
  }
  startThreadDetailLivenessWatchdog();
  evictIdleThreadDetailSubscriptionsToCapacity();

  return createThreadDetailSubscriptionRelease(entry);
}
